import { useCallback, useEffect, useState } from 'react'
import {
  Server, HardDrive, Router, Cctv, Home, Archive, ShieldCheck, Laptop,
  RefreshCw, Clock3,
} from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { useMe } from '../shell/AppShell.js'
import { fraicheur } from '../shell/status.js'
import { apiGet, adminMutate } from '../admin/primitives.js'
import { PAGE_CLE_PAR_SOURCE, SOURCES_FORMULAIRE_CLE } from '../admin/cle-acces-model.js'
import { Link } from '../router.js'

/**
 * LOT 8 — Cockpit Infrastructure. Le navigateur AFFICHE des états déjà
 * dérivés côté serveur (moteur unique) — aucune interprétation locale.
 * Aucun appel direct UniFi / HA / Protect ne part d'ici, jamais.
 */

/* ─────────────────────────────── types du modèle de lecture ─── */

interface SourceView {
  key: string
  source: string
  label: string
  state: 'HEALTHY' | 'DEGRADED' | 'UNAVAILABLE' | 'STALE' | 'UNKNOWN'
  state_label: string
  headline: string | null
  problems: string[]
  metrics: { key: string; label: string; value: number | string; unit: string | null }[]
  seconds_since_success: number | null
  stale: boolean
  showing_lkg: boolean
  /** La CLASSE de la panne : c'est elle qui dit si un geste est possible.
   *  Le libellé seul ne se teste pas — « Authentification refusée » est une
   *  phrase, `AUTH` est une décision. */
  last_error_class: string | null
  last_error_label: string | null
  components_total: number
  components_offline: number
  version: string | null
  has_detail_page: boolean
}

interface SourceDetail {
  view: SourceView
  snapshot: {
    components: { key: string; name: string; kind: string; state: 'ONLINE' | 'OFFLINE' | 'WARNING'; detail: string | null }[]
    metrics: { key: string; label: string; value: number | string; unit: string | null }[]
    problems: string[]
    version: string | null
  } | null
  snapshot_at: string | null
  refresh_interval_s: number
  consecutive_failures: number
  last_error: string | null
  can_technical_details: boolean
  changes: { component: string | null; component_name: string | null; from_state: string; to_state: string; detail: string | null; changed_at: string }[]
  active_alerts: { severity: string; title: string; occurred_at: string | null }[]
}

/* ───────────────────────────────────────── icônes + pastilles ─── */

const ICONS: Record<string, typeof Server> = {
  gestion: ShieldCheck, dell: HardDrive, 'unifi-network': Router,
  'unifi-protect': Cctv, 'home-assistant': Home, backups: Archive,
  'main-courante': Server, parc: Laptop,
}

const STATE_PILL: Record<string, string> = {
  HEALTHY: 'lc-pill--ok', DEGRADED: 'lc-pill--warning', STALE: 'lc-pill--warning',
  UNAVAILABLE: 'lc-pill--error', UNKNOWN: 'lc-pill--idle',
}
const COMPONENT_DOT: Record<string, string> = {
  ONLINE: 'lc-dot--ok', WARNING: 'lc-dot--warning', OFFLINE: 'lc-dot--error',
}
const COMPONENT_LABEL: Record<string, string> = {
  ONLINE: 'En ligne', WARNING: 'À surveiller', OFFLINE: 'Hors ligne',
}


/* ═══════════════════════════ PAGE DÉTAIL /infrastructure/:slug ═════════ */

export function InfraSourcePage({ slug }: { slug: string }) {
  const meta = useShellMeta()
  const me = useMe()
  const [d, setD] = useState<SourceDetail | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const charger = useCallback(() => {
    apiGet<SourceDetail>(`/api/v1/infrastructure/${slug}`).then((r) => { if ('view' in r) setD(r) }).catch(() => undefined)
  }, [slug])
  useEffect(() => {
    charger()
    const t = setInterval(charger, 30_000)
    return () => clearInterval(t)
  }, [charger])

  const canRefresh = me?.permissions.includes('sync.run') ?? false
  const refresh = async () => {
    setRefreshing(true)
    try {
      const session = await fetch('/api/v1/auth/session', { credentials: 'same-origin' }).then((r) => r.json()) as { csrf_token?: string }
      await fetch(`/api/v1/infrastructure/${slug}/refresh`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'x-csrf-token': session.csrf_token ?? '' },
      })
      setTimeout(() => { charger(); setRefreshing(false) }, 4000)
    } catch { setRefreshing(false) }
  }

  /*
   * RENOUVELER LA CLÉ — visible SEULEMENT quand c'est le problème.
   *
   * Un champ de saisie de secret affiché en permanence sur chaque source
   * invite à coller une clé là où elle ne sert à rien. Il n'apparaît donc que
   * lorsque la source a réellement répondu « authentification refusée ».
   */
  const [cle, setCle] = useState('')
  const [envoiCle, setEnvoiCle] = useState<'repos' | 'envoi' | 'ok' | 'annule' | 'droits' | 'echec'>('repos')
  const [detailEchec, setDetailEchec] = useState<string | null>(null)

  /*
   * REMPLACER UNE CLÉ EST UNE OPÉRATION SENSIBLE — d'où la confirmation forte.
   *
   * Le premier jet appelait `fetch` directement. La route répondait alors
   * `403 STEPUP_REQUIRED`, personne ne l'interprétait, et l'écran affichait un
   * « refusé, vérifiez vos droits » à quelqu'un qui avait tous les droits.
   * L'opération n'était pas bloquée par la sécurité : elle était bloquée par un
   * appel qui ne savait pas répondre à la sécurité.
   *
   * `adminMutate` EST le mécanisme canonique de Gestion : il reconnaît
   * `STEPUP_REQUIRED`, ouvre la modale de ré-authentification déjà utilisée
   * partout ailleurs, puis REJOUE la requête une seule fois. Une confirmation
   * encore fraîche n'est pas redemandée — la fraîcheur est jugée côté serveur,
   * il n'y a pas de règle spéciale ici.
   */
  const poserCle = async () => {
    if (cle.trim().length < 8) return
    setEnvoiCle('envoi'); setDetailEchec(null)
    try {
      const r = await adminMutate<{ error?: string; message?: string }>(
        'PUT', `/api/v1/infrastructure/${slug}/credential`, { api_key: cle.trim() },
      )
      if (r.status >= 200 && r.status < 300) {
        // Le champ est vidé AVANT tout le reste : le secret ne doit pas
        // séjourner dans le DOM une fois parti.
        setCle(''); setEnvoiCle('ok'); setTimeout(charger, 5000); return
      }
      // Toujours 403 STEPUP après la reprise : la confirmation a été annulée
      // ou refusée. Ce n'est pas un problème de droits, et le dire compte.
      if (r.status === 403 && r.data?.error === 'STEPUP_REQUIRED') { setEnvoiCle('annule'); return }
      if (r.status === 403 || r.status === 401) { setEnvoiCle('droits'); return }
      setDetailEchec(r.data?.message ?? null); setEnvoiCle('echec')
    } catch { setEnvoiCle('echec') }
  }

  const v = d?.view
  const Icon = ICONS[slug] ?? Server
  const pageCle = PAGE_CLE_PAR_SOURCE[slug] ?? null
  const f = v ? fraicheur(v.seconds_since_success, v.stale || v.state === 'UNAVAILABLE') : null

  return (
    <Shell active="infrastructure" meta={meta}>
      <main className="lc-page lc-stagger">
        <Link className="lc-crumb" href="/infrastructure">← Infrastructure</Link>
        {!v ? <p className="lc-hint" style={{ marginTop: 'var(--sp-5)' }}>Chargement…</p> : (
          <>
            <header className="infra-detail-head">
              <span className="infra-card__icon infra-card__icon--lg"><Icon size={24} aria-hidden="true" /></span>
              <div className="lc-stack">
                <h1 className="lc-title" style={{ margin: 0 }}>{v.label}</h1>
                {v.version ? <span className="lc-hint">Version {v.version}</span> : null}
              </div>
              <span className={`lc-pill ${v.last_error_class === 'SUPERVISION_UNAVAILABLE' && v.state !== 'HEALTHY' && v.state !== 'DEGRADED' ? 'lc-pill--idle' : STATE_PILL[v.state] ?? ''}`}>{v.state_label}</span>
              <span className="lc-panel__spacer" />
              <span className="lc-hint infra-card__fresh"><Clock3 size={13} aria-hidden="true" /> {f ?? 'Jamais collecté'}</span>
              {canRefresh ? (
                <button className="lc-btn" onClick={refresh} disabled={refreshing}>
                  <RefreshCw size={14} aria-hidden="true" className={refreshing ? 'infra-spin' : undefined} /> Collecter maintenant
                </button>
              ) : null}
            </header>

            {/* Formulaire de dépannage : seulement pour les sources qu'accepte la
                route de la supervision. Ailleurs (billetterie…), il répondait
                « rien à renouveler » à côté d'une clé refusée. */}
            {v.last_error_class === 'AUTH' && SOURCES_FORMULAIRE_CLE.has(slug) && (me?.permissions.includes('infrastructure.catalog.manage') ?? false) ? (
              <div className="adm-note" role="group" aria-label="Renouveler la clé d’API">
                <strong>L’identifiant enregistré n’est plus accepté par la source.</strong>{' '}
                Générez une nouvelle clé sur le contrôleur, puis collez-la ici. Gestion la met au
                coffre et relance aussitôt une collecte — l’état ne repassera au vert qu’après une
                lecture réellement réussie.
                <div className="infra-cle">
                  <input
                    type="password" className="lc-input" value={cle} autoComplete="off"
                    placeholder="Nouvelle clé d’API" aria-label="Nouvelle clé d’API"
                    onChange={(e) => { setCle(e.target.value); setEnvoiCle('repos') }}
                  />
                  <button className="lc-btn lc-btn--primary" onClick={() => { void poserCle() }}
                          disabled={cle.trim().length < 8 || envoiCle === 'envoi'}>
                    {envoiCle === 'envoi' ? 'Enregistrement…' : 'Enregistrer et collecter'}
                  </button>
                </div>
                {envoiCle === 'ok' ? <span className="lc-hint">Clé enregistrée. Vérification de la connexion à {v.label} en cours — l’état se met à jour dans quelques secondes.</span> : null}
                {envoiCle === 'annule' ? <span className="lc-hint">Confirmation forte annulée — la clé n’a pas été enregistrée.</span> : null}
                {envoiCle === 'droits' ? <span className="lc-hint">Vos droits ne permettent pas de remplacer cette clé.</span> : null}
                {envoiCle === 'echec' ? <span className="lc-hint">{detailEchec ?? 'L’enregistrement a échoué.'}</span> : null}
                {pageCle ? (
                  <span className="lc-hint">
                    Pour essayer une clé avant de l’enregistrer, ou changer de clé sans attendre une panne :{' '}
                    <Link href={pageCle}>Administration → Intégrations → {v.label}</Link>.
                  </span>
                ) : null}
              </div>
            ) : null}

            {/* Clé refusée sur une source sans formulaire ici (billetterie) :
                le remède est sur la page de l'intégration, pas sur celle-ci. */}
            {v.last_error_class === 'AUTH' && !SOURCES_FORMULAIRE_CLE.has(slug) && pageCle && (me?.permissions.includes('integration.read') ?? false) ? (
              <div className="adm-note" role="group" aria-label="Remplacer la clé d’accès">
                <strong>L’identifiant enregistré n’est plus accepté par la source.</strong>{' '}
                Remplacez la clé depuis <Link href={pageCle}>Administration → Intégrations → {v.label}</Link> :
                vous pourrez l’essayer avant de l’enregistrer.
              </div>
            ) : null}

            {/* Hors panne, la clé se change depuis la page de l'intégration
                (panneau « Clé d'accès », avec essai avant enregistrement).
                L'adresse vient d'une table : /infrastructure/billetterie mène
                à /settings/integrations/shotgun, pas à une page inexistante. */}
            {v.last_error_class !== 'AUTH' && pageCle && (me?.permissions.includes('integration.read') ?? false) ? (
              <p className="lc-hint">
                Clé d’accès de cette source : <Link href={pageCle}>Administration → Intégrations → {v.label}</Link>
              </p>
            ) : null}

            {v.showing_lkg ? (
              <div className="adm-note" role="status">
                Cette source ne répond plus{v.last_error_label ? ` (${v.last_error_label.toLowerCase()})` : ''} —
                Gestion affiche les dernières données fiables, sans les effacer.
              </div>
            ) : null}

            {v.headline || v.problems.length > 0 ? (
              <section className="lc-panel gd-panel infra-headline">
                <div className="lc-panel__body lc-stack">
                  {v.headline ? <span className="infra-headline__main">{v.headline}</span> : null}
                  {v.problems.filter((p) => p !== v.headline).map((p, i) => (
                    <span key={i} className="lc-hint">⚠ {p}</span>
                  ))}
                </div>
              </section>
            ) : null}

            {(d?.snapshot?.metrics.length ?? 0) > 0 ? (
              <section className="infra-metrics">
                {d!.snapshot!.metrics.map((m) => (
                  <div key={m.key} className="infra-metric">
                    <span className="infra-metric__value">{m.value}{m.unit === '%' ? ' %' : ''}</span>
                    <span className="lc-hint">{m.label}{m.unit && m.unit !== '%' ? ` (${m.unit})` : ''}</span>
                  </div>
                ))}
              </section>
            ) : null}

            {(d?.snapshot?.components.length ?? 0) > 0 ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Composants</h2></header>
                <table className="lc-table">
                  <thead><tr><th>Composant</th><th>État</th><th>Détail</th></tr></thead>
                  <tbody>
                    {d!.snapshot!.components.map((c) => (
                      <tr key={c.key}>
                        <td>{c.name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`lc-dot ${COMPONENT_DOT[c.state] ?? ''}`} aria-hidden="true" style={{ marginRight: 8, display: 'inline-block' }} />
                          {COMPONENT_LABEL[c.state] ?? c.state}
                        </td>
                        <td className="lc-hint">{c.detail ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ) : null}

            {(d?.active_alerts.length ?? 0) > 0 ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Alertes actives</h2></header>
                <div className="lc-panel__body lc-stack">
                  {d!.active_alerts.map((a, i) => (
                    <span key={i}><span className={`lc-dot ${a.severity === 'CRITICAL' ? 'lc-dot--error' : 'lc-dot--warning'}`} aria-hidden="true" style={{ marginRight: 8, display: 'inline-block' }} />{a.title}</span>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="lc-panel gd-panel">
              <header className="lc-panel__head"><h2 className="lc-section-title">Historique récent</h2></header>
              {(d?.changes.length ?? 0) === 0 ? (
                <div className="lc-panel__body"><span className="lc-hint">Aucun changement d’état enregistré.</span></div>
              ) : (
                <table className="lc-table">
                  <thead><tr><th>Quand</th><th>Quoi</th><th>Changement</th></tr></thead>
                  <tbody>
                    {d!.changes.map((c, i) => (
                      <tr key={i}>
                        <td className="lc-hint" style={{ whiteSpace: 'nowrap' }}>{quand(c.changed_at)}</td>
                        <td>{c.component_name ?? v.label}</td>
                        <td>
                          <span className="lc-stack">
                            <span>{etatCourt(c.from_state)} → {etatCourt(c.to_state)}</span>
                            {c.detail ? <span className="lc-hint">{c.detail}</span> : null}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {d?.can_technical_details && d.last_error ? (
              <section className="lc-panel gd-panel">
                <header className="lc-panel__head"><h2 className="lc-section-title">Détails techniques</h2></header>
                <div className="lc-panel__body lc-stack">
                  <span className="lc-hint">Dernière erreur de collecte ({d.consecutive_failures} échec{d.consecutive_failures > 1 ? 's' : ''} consécutif{d.consecutive_failures > 1 ? 's' : ''}) :</span>
                  <code className="infra-error">{d.last_error}</code>
                </div>
              </section>
            ) : null}
          </>
        )}
      </main>
    </Shell>
  )
}

function quand(iso: string): string {
  const d = new Date(iso)
  const aujourdHui = new Date().toDateString() === d.toDateString()
  const heure = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' })
  return aujourdHui ? heure : `${d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'Europe/Paris' })} ${heure}`
}

function etatCourt(s: string): string {
  const map: Record<string, string> = {
    HEALTHY: 'Opérationnel', DEGRADED: 'Dégradé', UNAVAILABLE: 'Indisponible',
    STALE: 'Données anciennes', UNKNOWN: 'Inconnu',
    ONLINE: 'En ligne', OFFLINE: 'Hors ligne', WARNING: 'À surveiller',
  }
  return map[s] ?? s
}
